import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../../components/Breadcrumbs";
import humanoidCompanies from "../../data/humanoid-company-directory.json";
import { slugifyDirectoryValue } from "../../utils/directoryUtils";

const PAGE_SIZE = 36;

const flagByCode = {
  AT: "🇦🇹",
  AU: "🇦🇺",
  CA: "🇨🇦",
  CH: "🇨🇭",
  CN: "🇨🇳",
  DE: "🇩🇪",
  ES: "🇪🇸",
  FR: "🇫🇷",
  GB: "🇬🇧",
  HK: "🇭🇰",
  HU: "🇭🇺",
  IL: "🇮🇱",
  IN: "🇮🇳",
  IT: "🇮🇹",
  JP: "🇯🇵",
  KR: "🇰🇷",
  NO: "🇳🇴",
  PL: "🇵🇱",
  RU: "🇷🇺",
  SG: "🇸🇬",
  TR: "🇹🇷",
  TW: "🇹🇼",
  US: "🇺🇸",
  VN: "🇻🇳",
};

const typeGroups = {
  direct: [
    "Humanoid manufacturer",
    "Humanoid developer",
    "Social humanoid company",
    "Service humanoid company",
    "Android developer",
    "Service robot developer",
    "Humanoid development program",
    "Social robot company",
  ],
  research: [
    "Humanoid research program",
    "Open-source research project",
    "Open-source humanoid developer",
    "Robotics research company",
    "Avatar robotics developer",
    "Physical AI developer",
  ],
  ecosystem: [
    "Humanoid component company",
    "Humanoid ecosystem company",
    "Robotics ecosystem company",
    "Industrial robotics company",
    "Wearable robotics company",
    "Bionic component company",
  ],
};

function isInTypeGroup(company, group) {
  return !group || typeGroups[group]?.includes(company.companyType);
}

function sourceLabel(company) {
  if (company.verificationLevel === "official-source") return "Official source";
  return "Industry source";
}

function buildDirectorySchema() {
  const base = "https://www.techniahqservice.com";
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Global Humanoid Robot Companies Directory",
    description:
      "A filterable directory of humanoid robot manufacturers, developers, research programs, component companies and robotics ecosystem suppliers.",
    url: `${base}/humanoid-robots/manufacturers`,
    dateModified: "2026-09-03",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: humanoidCompanies.length,
      itemListElement: humanoidCompanies.map((company, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Organization",
          name: company.name,
          url: company.officialUrl || company.sourceUrl,
          address: company.country
            ? {
                "@type": "PostalAddress",
                addressCountry: company.countryCode,
                addressLocality: company.headquarters || undefined,
              }
            : undefined,
        },
      })),
    },
  };
}

export default function HumanoidManufacturersPage() {
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("");
  const [typeGroup, setTypeGroup] = useState("");
  const [verification, setVerification] = useState("");
  const [page, setPage] = useState(1);

  const countries = useMemo(
    () =>
      [...new Set(humanoidCompanies.map((company) => company.country))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [],
  );

  const filteredCompanies = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return humanoidCompanies.filter((company) => {
      if (country && company.country !== country) return false;
      if (!isInTypeGroup(company, typeGroup)) return false;
      if (verification && company.verificationLevel !== verification) return false;
      if (!normalizedQuery) return true;
      return [
        company.name,
        company.country,
        company.headquarters,
        company.companyType,
        company.robotPrograms,
        company.status,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedQuery));
    });
  }, [country, query, typeGroup, verification]);

  const pageCount = Math.max(1, Math.ceil(filteredCompanies.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visibleCompanies = filteredCompanies.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const directCompanyCount = humanoidCompanies.filter((company) =>
    isInTypeGroup(company, "direct"),
  ).length;
  const officialSourceCount = humanoidCompanies.filter(
    (company) => company.verificationLevel === "official-source",
  ).length;

  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Humanoid robots", to: "/humanoid-robots" },
    { label: "Companies", to: "/humanoid-robots/manufacturers" },
  ];

  const resetFilters = () => {
    setQuery("");
    setCountry("");
    setTypeGroup("");
    setVerification("");
    setPage(1);
  };

  const updateFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  return (
    <>
      <SEO
        title="Humanoid Robot Companies Worldwide TechniaHQRobot"
        description={`Search ${humanoidCompanies.length} humanoid robot manufacturers, developers, research programs and ecosystem companies across ${countries.length} countries.`}
        path="/humanoid-robots/manufacturers"
        schema={[buildDirectorySchema(), buildBreadcrumbSchema(breadcrumbs)]}
      />
      <Breadcrumbs items={breadcrumbs} />

      <div>
        <section className="border-b border-neutral-200 bg-neutral-950 py-14 text-white sm:py-20">
          <div className="site-shell">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-blue-300">
              Global company directory
            </p>
            <h1 className="mt-4 max-w-5xl text-4xl font-black tracking-tight sm:text-6xl">
              Humanoid Robot Companies Worldwide
            </h1>
            <p className="mt-6 max-w-4xl text-lg leading-8 text-neutral-300">
              A searchable directory of companies building humanoid robots, robot bodies,
              dexterous hardware and embodied-AI systems. Filter by country, company type
              or source level to find the relevant organization.
            </p>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[
                [humanoidCompanies.length, "directory records"],
                [directCompanyCount, "manufacturers and developers"],
                [countries.length, "countries and markets"],
                [officialSourceCount, "records with official sources"],
              ].map(([value, label]) => (
                <div key={label} className="rounded-2xl border border-white/15 bg-white/5 p-5">
                  <p className="text-3xl font-black">{value}</p>
                  <p className="mt-1 text-sm font-bold text-neutral-300">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="site-shell py-10 sm:py-14">
          <div className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-7">
            <div className="grid gap-5 lg:grid-cols-4">
              <label className="block text-sm font-black text-neutral-800 lg:col-span-2">
                Search a company or robot
                <input
                  type="search"
                  value={query}
                  onChange={updateFilter(setQuery)}
                  placeholder="Figure AI, Unitree, Walker S, France…"
                  className="mt-2 w-full rounded-xl border border-neutral-300 px-4 py-3 font-medium outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                />
              </label>

              <label className="block text-sm font-black text-neutral-800">
                Country
                <select
                  value={country}
                  onChange={updateFilter(setCountry)}
                  className="mt-2 w-full rounded-xl border border-neutral-300 px-4 py-3 font-medium"
                >
                  <option value="">All countries</option>
                  {countries.map((countryName) => {
                    const code = humanoidCompanies.find(
                      (company) => company.country === countryName,
                    )?.countryCode;
                    return (
                      <option key={countryName} value={countryName}>
                        {flagByCode[code] || "🌐"} {countryName}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label className="block text-sm font-black text-neutral-800">
                Company scope
                <select
                  value={typeGroup}
                  onChange={updateFilter(setTypeGroup)}
                  className="mt-2 w-full rounded-xl border border-neutral-300 px-4 py-3 font-medium"
                >
                  <option value="">All company types</option>
                  <option value="direct">Robot manufacturers and developers</option>
                  <option value="research">Research and physical AI</option>
                  <option value="ecosystem">Components and industrial ecosystem</option>
                </select>
              </label>
            </div>

            <div className="mt-5 flex flex-col gap-4 border-t border-neutral-200 pt-5 sm:flex-row sm:items-end sm:justify-between">
              <label className="block text-sm font-black text-neutral-800">
                Source level
                <select
                  value={verification}
                  onChange={updateFilter(setVerification)}
                  className="mt-2 w-full rounded-xl border border-neutral-300 px-4 py-3 font-medium sm:w-64"
                >
                  <option value="">Official and industry sources</option>
                  <option value="official-source">Official company source</option>
                  <option value="secondary-source">Industry directory source</option>
                </select>
              </label>

              <div className="flex items-center gap-4">
                <p className="text-sm font-bold text-neutral-600">
                  {filteredCompanies.length} compan{filteredCompanies.length === 1 ? "y" : "ies"}
                </p>
                <button type="button" onClick={resetFilters} className="btn-secondary">
                  Reset filters
                </button>
              </div>
            </div>
          </div>

          <div className="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
            <strong>Classification rule</strong> “Manufacturer” and “developer” identify direct
            humanoid work. Component suppliers, research programs, social robots and industrial
            partners remain visible, but they are labeled separately. Records marked “Industry
            source” require another official check before publishing a precise product claim.
          </div>

          {visibleCompanies.length ? (
            <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {visibleCompanies.map((company) => (
                <article key={company.id} className="soft-card soft-card-hover flex h-full flex-col p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-700">
                        {company.companyType}
                      </p>
                      <h2 className="mt-2 text-2xl font-black leading-tight">{company.name}</h2>
                    </div>
                    <span className="text-2xl" aria-label={company.country}>
                      {flagByCode[company.countryCode] || "🌐"}
                    </span>
                  </div>

                  <p className="mt-3 text-sm font-bold text-neutral-500">
                    {company.country !== "Global / United States" ? (
                      <Link
                        to={`/robotics-companies/country/${slugifyDirectoryValue(company.country)}`}
                        className="hover:text-blue-700 hover:underline"
                      >
                        {company.country}
                      </Link>
                    ) : company.country}
                    {company.headquarters ? ` · ${company.headquarters}` : ""}
                  </p>
                  {company.localName ? (
                    <p className="mt-2 text-sm font-semibold text-neutral-600">{company.localName}</p>
                  ) : null}

                  <div className="mt-5 flex-1 space-y-4">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-neutral-500">
                        Robot or program
                      </p>
                      <p className="mt-1 leading-7 text-neutral-700">
                        {company.robotPrograms || "Humanoid robotics program"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-neutral-500">
                        Public status
                      </p>
                      <p className="mt-1 font-bold text-neutral-800">{company.status}</p>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 pt-5">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-black ${
                        company.verificationLevel === "official-source"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-900"
                      }`}
                    >
                      {sourceLabel(company)}
                    </span>

                    {company.catalogSlug ? (
                      <Link
                        to={`/humanoid-robots/manufacturer/${company.catalogSlug}`}
                        className="font-black text-blue-700 hover:underline"
                      >
                        View catalog profile →
                      </Link>
                    ) : (
                      <a
                        href={company.officialUrl || company.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="font-black text-blue-700 hover:underline"
                      >
                        {company.officialUrl ? "Official website ↗" : "Open source ↗"}
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-8 rounded-3xl border border-dashed border-neutral-300 bg-neutral-50 p-12 text-center">
              <h2 className="text-2xl font-black">No company matches these filters</h2>
              <button type="button" onClick={resetFilters} className="btn-primary mt-5">
                Show the full directory
              </button>
            </div>
          )}

          {pageCount > 1 && (
            <nav className="mt-10 flex flex-wrap justify-center gap-2" aria-label="Company directory pagination">
              {Array.from({ length: pageCount }, (_, index) => index + 1).map((pageNumber) => (
                <button
                  type="button"
                  key={pageNumber}
                  onClick={() => {
                    setPage(pageNumber);
                    window.scrollTo({ top: 520, behavior: "smooth" });
                  }}
                  className={`h-11 min-w-11 rounded-xl border px-3 font-black ${
                    pageNumber === safePage
                      ? "border-neutral-950 bg-neutral-950 text-white"
                      : "border-neutral-300 bg-white text-neutral-800 hover:border-neutral-500"
                  }`}
                  aria-current={pageNumber === safePage ? "page" : undefined}
                >
                  {pageNumber}
                </button>
              ))}
            </nav>
          )}

          <section className="mt-14 grid gap-6 border-t border-neutral-200 pt-10 lg:grid-cols-2">
            <div className="rounded-3xl bg-neutral-950 p-7 text-white">
              <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-300">
                Reading the directory
              </p>
              <h2 className="mt-3 text-3xl font-black">One record per company</h2>
              <p className="mt-4 leading-7 text-neutral-300">
                Each entry identifies the country, company type, robot program and recorded
                public status. Open its source to check the scope and date of the information.
              </p>
            </div>

            <div className="rounded-3xl border border-neutral-200 bg-neutral-50 p-7">
              <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-700">
                Detailed robot catalog
              </p>
              <h2 className="mt-3 text-3xl font-black">Models, prices and specifications</h2>
              <p className="mt-4 leading-7 text-neutral-600">
                Open a model profile for dimensions, configuration notes, price sources and
                availability. A company listing alone does not establish the capabilities or
                availability of its robots.
              </p>
              <Link to="/humanoid-robots" className="btn-primary mt-6 inline-flex">
                Open the humanoid robot catalog
              </Link>
            </div>
          </section>
        </section>
      </div>
    </>
  );
}
